import {
  $getRoot,
  $isElementNode,
  $isTextNode,
  type LexicalNode,
} from 'lexical';
import { $isBeautifulMentionNode } from 'lexical-beautiful-mentions';

export const VISIBLE_CONSECUTIVE_MENTIONS = 5;

export type MentionOverflowHiddenItem = {
  key: string;
  label: string;
  mentionMenuItemId?: string;
  entityId?: string;
};

export type MentionOverflowEntry =
  | { kind: 'visible' }
  | { kind: 'hidden' }
  | {
      kind: 'overflowAnchor';
      overflowMentions: MentionOverflowHiddenItem[];
    };

export type MentionOverflowMap = Record<string, MentionOverflowEntry>;

type MentionNodeInfo = {
  key: string;
  label: string;
  mentionMenuItemId?: string;
  entityId?: string;
};

const isIgnorableBetweenMentions = (node: LexicalNode): boolean => {
  if (!$isTextNode(node)) {
    return false;
  }
  return node.getTextContent().trim().length === 0;
};

const collectMentionNodesInOrder = (
  node: LexicalNode,
  result: Array<MentionNodeInfo | 'break'>,
): void => {
  if ($isBeautifulMentionNode(node)) {
    const data = node.getData() ?? {};
    const mentionMenuItemId =
      typeof data.mentionMenuItemId === 'string'
        ? data.mentionMenuItemId
        : undefined;
    const entityId =
      typeof data.entityId === 'string' ? data.entityId : undefined;
    result.push({
      key: node.getKey(),
      label: node.getValue(),
      mentionMenuItemId,
      entityId,
    });
    return;
  }

  if ($isTextNode(node)) {
    if (!isIgnorableBetweenMentions(node)) {
      result.push('break');
    }
    return;
  }

  if ($isElementNode(node)) {
    const children = node.getChildren();
    for (let index = 0; index < children.length; index++) {
      const child = children[index];
      if (index > 0 && $isElementNode(child) && !child.isInline()) {
        result.push('break');
      }
      collectMentionNodesInOrder(child, result);
    }
  }
};

export const buildMentionOverflowMap = (): MentionOverflowMap => {
  const ordered: Array<MentionNodeInfo | 'break'> = [];
  collectMentionNodesInOrder($getRoot(), ordered);

  const map: MentionOverflowMap = {};
  let currentRun: MentionNodeInfo[] = [];

  const flushRun = () => {
    if (currentRun.length === 0) {
      return;
    }

    if (currentRun.length <= VISIBLE_CONSECUTIVE_MENTIONS) {
      for (const mention of currentRun) {
        map[mention.key] = { kind: 'visible' };
      }
      currentRun = [];
      return;
    }

    const visible = currentRun.slice(0, VISIBLE_CONSECUTIVE_MENTIONS);
    const hidden = currentRun.slice(VISIBLE_CONSECUTIVE_MENTIONS);
    const anchor = visible[visible.length - 1];

    for (const mention of visible.slice(0, -1)) {
      map[mention.key] = { kind: 'visible' };
    }

    map[anchor.key] = {
      kind: 'overflowAnchor',
      overflowMentions: hidden.map((mention) => ({
        key: mention.key,
        label: mention.label,
        mentionMenuItemId: mention.mentionMenuItemId,
        entityId: mention.entityId,
      })),
    };

    for (const mention of hidden) {
      map[mention.key] = { kind: 'hidden' };
    }

    currentRun = [];
  };

  for (const entry of ordered) {
    if (entry === 'break') {
      flushRun();
      continue;
    }
    currentRun.push(entry);
  }
  flushRun();

  return map;
};
